import React, { createContext, useState, useEffect, useContext } from 'react';
import { loginApi, registerApi, logoutApi, refreshTokenApi } from '../api/authApi';
import { businessApi } from '../api/businessApi';
import { setAuthToken, removeAuthToken, getAuthToken } from '../utils/helpers';

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [activeBusiness, setActiveBusiness] = useState(null);

  const loadBusinesses = async () => {
    try {
      const res = await businessApi.getBusinesses();
      if (res.data.success && res.data.data.length > 0) {
        setBusinesses(res.data.data);
        const storedId = localStorage.getItem('activeBusinessId');
        let selected = res.data.data.find(b => b._id === storedId);
        if (!selected) {
          selected = res.data.data[0];
          localStorage.setItem('activeBusinessId', selected._id);
        }
        setActiveBusiness(selected);
      }
    } catch (err) {
      console.error('Failed to load businesses', err);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();
      if (token) {
        try {
          const response = await refreshTokenApi();
          setUser(response.data.data.user);
          if (response.data.data.tokens) {
            setAuthToken(response.data.data.tokens.accessToken);
          }
          await loadBusinesses();
        } catch (error) {
          removeAuthToken();
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await loginApi(email, password);
      const { user, tokens } = response.data.data;
      
      setAuthToken(tokens.accessToken);
      setUser(user);
      await loadBusinesses();
      
      return { success: true };
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
      return { success: false, error: error.response?.data?.message };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await registerApi(userData);
      return { success: true, data: response.data };
    } catch (error) {
      setError(error.response?.data?.message || 'Registration failed');
      return { success: false, error: error.response?.data?.message };
    }
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      removeAuthToken();
      localStorage.removeItem('activeBusinessId');
      setUser(null);
      setBusinesses([]);
      setActiveBusiness(null);
    }
  };

  const switchBusiness = (businessId) => {
    const selected = businesses.find(b => b._id === businessId);
    if (selected) {
      localStorage.setItem('activeBusinessId', businessId);
      setActiveBusiness(selected);
      // Hard reload to refresh all context/state based on new business
      window.location.reload();
    }
  };

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    register,
    logout,
    businesses,
    activeBusiness,
    switchBusiness,
    loadBusinesses,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isOwner: user?.role === 'owner' || user?.role === 'user' || user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
